export const ISRAELI_PHONE_ERROR = "יש להזין מספר טלפון ישראלי תקין.";
export const ISRAELI_BUSINESS_ID_ERROR = "יש להזין מספר עוסק / ח.פ. / ת״ז תקין.";
export const BILLING_EMAIL_ERROR = "יש להזין כתובת אימייל תקינה לקבלת מסמכי חיוב.";

/**
 * Normalizes an Israeli telephone number to local digits-only form.
 *
 * Accepted input may contain spaces, hyphens and parentheses and may use
 * +972 / 00972 / 972 international prefixes. The returned value is always
 * domestic (leading 0) and digits-only.
 */
export function normalizeIsraeliPhone(value: string): string | null {
  const raw = value.trim();
  if (!raw || !/^[+()0-9\s-]+$/.test(raw)) return null;

  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("00972")) {
    digits = `0${digits.slice(5)}`;
  } else if (digits.startsWith("972")) {
    digits = `0${digits.slice(3)}`;
  }

  if (/^05\d{8}$/.test(digits)) return digits;
  if (/^07\d{8}$/.test(digits)) return digits;
  if (/^0[23489]\d{7}$/.test(digits)) return digits;
  return null;
}

export function isValidIsraeliPhone(value: string): boolean {
  return normalizeIsraeliPhone(value) !== null;
}

/**
 * Normalizes the identifier used for an Israeli business account.
 *
 * Sole proprietors commonly use the owner's Israeli identity number as their
 * dealer identifier. Older/short-form identity numbers may be typed without
 * their leading zeroes, so values shorter than 9 digits are left-padded.
 * Israeli company identifiers (ח.פ./ח.צ.) are 9 digits and begin with 5.
 */
export function normalizeIsraeliBusinessId(value: string): string | null {
  const raw = value.trim();
  if (!raw || !/^[0-9\s-]+$/.test(raw)) return null;

  const digits = raw.replace(/\D/g, "");
  if (digits.length < 5 || digits.length > 9) return null;

  return digits.padStart(9, "0");
}

/**
 * Structural validation for the identifier stored on an Israeli business
 * account. This accepts either:
 * - an Israeli identity/dealer number with a valid Mod-10 check digit; or
 * - a 9-digit Israeli company identifier beginning with 5.
 *
 * This is structural validation only and does not prove that an entity is
 * currently registered or active with the Tax Authority / Companies Authority.
 */
export function isValidIsraeliBusinessId(value: string): boolean {
  const digits = normalizeIsraeliBusinessId(value);
  if (!digits || /^0{9}$/.test(digits)) return false;

  if (/^5\d{8}$/.test(digits)) return true;

  let sum = 0;
  for (let index = 0; index < digits.length; index += 1) {
    let product = Number(digits[index]) * (index % 2 === 0 ? 1 : 2);
    if (product > 9) product -= 9;
    sum += product;
  }
  return sum % 10 === 0;
}

export function normalizeValidIsraeliBusinessId(value: string): string | null {
  const normalized = normalizeIsraeliBusinessId(value);
  return normalized && isValidIsraeliBusinessId(normalized) ? normalized : null;
}

export function normalizeBillingEmail(value: string): string | null {
  const email = value.trim();
  if (!email || email.length > 254 || /\s/.test(email)) return null;
  if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) return null;
  return email;
}

export function isValidBillingEmail(value: string): boolean {
  return normalizeBillingEmail(value) !== null;
}
